import { getRandomColor, isLightColor } from 'utils/color';
import sample from 'utils/sample';
import {
  getLayoutType,
  getMaximumNumberOfPages,
  getOriginalPage,
  isPhotoPlaceholder,
  isTextPlaceholder,
  TEMPLATE_LAYOUT,
} from 'editor-utils/template';
import { generatePage } from './utils';

/**
 * Generate Dummy data only for testing.
 * @param {object} template
 * @param {string} pageId
 * @returns {{ data: { pages: Array, photos: Array }}}
 */
export const generateDummyFontData = async (template, pageId = '') => {
  const pages = [];
  const maximumNumberOfPages = getMaximumNumberOfPages(template);

  if (template.cover) {
    // GENERATE COVER
    let cover = template.cover.find(page => page.id === pageId);
    if (!cover) {
      cover = sample(template.cover);
    }
    pages.push(generatePage(cover));
  }

  // GENERATE PAGES
  if (getLayoutType(template) === TEMPLATE_LAYOUT.FIXED) {
    template.pages.forEach(page => {
      pages.push(generatePage(page));
    });
  } else if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE) {
    // GENERATE PAGES
    let page;
    if (pageId.length > 0) {
      page = template.pages.find(p => p.id === pageId);
    }
    if (!page) {
      page = sample(template.pages);
    }
    pages.push(generatePage(page));
  } else {
    for (let i = 1; i <= maximumNumberOfPages; ) {
      if (i % 2 !== 0 && i >= 50) {
        break;
      }

      let page;
      do {
        page = sample(template.pages);
      } while (
        (page.span === 1 && (i % 2 === 1 || i === maximumNumberOfPages)) ||
        !page.placeholders
      );
      // spanned page could be only on left side and not as last page

      pages.push(generatePage(page));
      if (page.span === 1) {
        i += 2;
      } else {
        i += 1;
      }
    }
  }

  const photos = [];

  pages.forEach((page, index) => {
    const originalPage = getOriginalPage(template, page);

    const numberOfItems = template.fonts.length;
    const numberOfColumns = 3;
    const numberOfRows = Math.ceil(numberOfItems / numberOfColumns);

    const itemHeight = (originalPage.size.height - 2) / numberOfRows;
    const itemWidth = (originalPage.size.width - 2) / numberOfColumns;

    const fontSize = Math.floor((((itemHeight / 2.54) * 72) / 8) * 0.6);

    let fontIndex = 0;
    const placeholders = [];
    for (let x = 0; x < numberOfColumns; x += 1) {
      for (let y = 0; y < numberOfRows; y += 1) {
        const font = template.fonts[fontIndex];

        if (!font) {
          break;
        }

        const bg = getRandomColor().toHex();
        const fg = isLightColor(bg) ? '#000000' : '#ffffff';

        placeholders.push({
          id: `v-${font.name}`,
          type: 'vector',
          override: {
            id: `v-${font.name}`,
            type: 'vector',
            element: 'rectangle',
            color: `#${bg}`,
            frame: [1 + x * itemWidth, 1 + y * itemHeight, itemWidth, itemHeight],
          },
        });

        placeholders.push({
          id: font.name,
          type: 'text',
          data: {
            value: `${font.name}\n\nabcdef   ghijklmnopqr   stuvwxyz\nABCDEFGHIJKL      MNOPQRSTUVWXYZ    0123456789,.;\nabcdef   ghijklmnopqr   stuvwxyz\nABCDEFGHIJKL      MNOPQRSTUVWXYZ    0123456789,.;ABCDEFGHIJKL      MNOPQRSTUVWXYZ    0123456789,.;\n(1900 - 2000) Find the difference between pictures or other images!\nPodle zákona o evidenci tržeb je prodávající povinen vystavit kupujícímu účtenku. Zároveň je povinen zaevidovat přijatou tržbu u správce daně online; v případě technického výpadku pak nejpozději do 48 hodin. Enter two images and the difference will show up below.`,
          },
          override: {
            id: font.name,
            type: 'text',
            alignment: 'left',
            color: fg,
            fontSize,
            fontName: font.name,
            frame: [1 + x * itemWidth, 1 + y * itemHeight, itemWidth, itemHeight],
            verticalAlignment: 'top',
          },
        });

        fontIndex += 1;
      }
    }
    pages[index].placeholders = placeholders;
  });

  return {
    data: {
      pages,
      photos,
    },
  };
};

/**
 * Generate Dummy data only for testing.
 * @param {object} template
 * @param {string} pageId
 * @returns {{ data: { pages: Array, photos: Array }}}
 */
const generateDummyData = async (template, pageId = '') => {
  const pages = [];
  const maximumNumberOfPages = getMaximumNumberOfPages(template);

  if (template.cover) {
    // GENERATE COVER
    let cover = template.cover.find(page => page.id === pageId);
    if (!cover) {
      cover = sample(template.cover);
    }
    pages.push(generatePage(cover));
  }

  // GENERATE PAGES
  if (getLayoutType(template) === TEMPLATE_LAYOUT.FIXED) {
    template.pages.forEach(page => {
      pages.push(generatePage(page));
    });
  } else if (getLayoutType(template) === TEMPLATE_LAYOUT.PAGE) {
    // GENERATE PAGES
    let page;
    if (pageId.length > 0) {
      page = template.pages.find(p => p.id === pageId);
    }
    if (!page) {
      page = sample(template.pages);
    }
    pages.push(generatePage(page));
  } else {
    for (let i = 1; i <= maximumNumberOfPages; ) {
      if (i % 2 !== 0 && i >= 50) {
        break;
      }

      let page;
      do {
        page = sample(template.pages);
      } while (
        (page.span === 1 && (i % 2 === 1 || i === maximumNumberOfPages)) ||
        !page.placeholders
      );
      // spanned page could be only on left side and not as last page

      pages.push(generatePage(page));
      if (page.span === 1) {
        i += 2;
      } else {
        i += 1;
      }
    }
  }

  const photos = [];

  const textValue = placeholderId => {
    if (placeholderId === 'children-caption') {
      return 'Children';
    }
    return placeholderId;
  };

  //
  // ADD PHOTOS AND TEXTS
  //
  pages.forEach((page, index) => {
    page.placeholders.forEach((placeholder, idx) => {
      if (isPhotoPlaceholder(placeholder)) {
        const key = placeholder.id;
        const width = 400;
        const height = 400;

        let bg = getRandomColor().toHex();
        let fg = isLightColor(bg) ? '#000000' : '#ffffff';
        bg = bg.replace('#', '');
        fg = fg.replace('#', '');

        const url = `https://dummyimage.com/${width}x${height}/${bg}/${fg}.jpg&text=++${key}++`;

        const photo = {
          alive: 1,
          tags: [],
          datetime: 'N/A',
          height,
          width,
          key: `${index}-${key}`,
          validDomain: true,
          url,
        };

        photos.push(photo);

        pages[index].placeholders[idx] = {
          id: placeholder.id,
          type: placeholder.type,
          data: {
            photo_key: photo.key,
            x: 0.5,
            y: 0.5,
            scale: 1,
            orientation: 0,
          },
        };
      } else if (isTextPlaceholder(placeholder)) {
        pages[index].placeholders[idx] = {
          id: placeholder.id,
          type: placeholder.type,
          data: {
            value: textValue(placeholder.id),
          },
        };
      }
    });

    pages[index].placeholders.push({
      id: 'debug-test-label',
      type: 'text',
      data: {
        value: `sku: ${template.id}    version: ${template.version}    page: ${page.id}`,
      },
      override: {
        id: 'debug-test-label',
        type: 'text',
        alignment: 'left',
        color: '#444444',
        fontSize: 14,
        fontName: 'Poppins-Bold',
        frame: [0.3, 0.3, 50, 5],
        verticalAlignment: 'top',
      },
    });
  });

  return {
    data: {
      pages,
      photos,
    },
  };
};

export default generateDummyData;
